import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Shield, Eye, EyeOff, Loader2 } from "lucide-react";
import { API } from "../App";

const AdminLogin = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);

  // Check if already logged in
  useEffect(() => {
    const checkAdminSession = async () => {
      try {
        // Try with stored token as backup
        const storedToken = localStorage.getItem('admin_session_token');
        const headers = storedToken ? { 'Authorization': `Bearer ${storedToken}` } : {};
        
        const response = await fetch(`${API}/admin/me`, {
          credentials: "include",
          headers,
        });
        
        if (response.ok) {
          navigate("/administrador/dashboard", { replace: true });
        } else {
          // Clear invalid token
          localStorage.removeItem('admin_session_token');
        }
      } catch (error) {
        // Not logged in, stay on login page
      } finally {
        setCheckingSession(false);
      }
    };

    checkAdminSession();
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API}/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email, password }),
      });

      if (response.ok) {
        const data = await response.json();
        // Store token in localStorage as backup
        if (data.session_token) {
          localStorage.setItem('admin_session_token', data.session_token);
        }
        toast.success(`Bienvenido, ${data.name}`);
        navigate("/administrador/dashboard", { replace: true });
      } else {
        const error = await response.json();
        toast.error(error.detail || "Credenciales inválidas");
      }
    } catch (error) {
      console.error("Login error:", error);
      toast.error("Error al iniciar sesión");
    } finally {
      setLoading(false);
    }
  };

  if (checkingSession) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-violet-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-slate-400">Verificando sesión...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/okchat-logo.png" alt="OkChat" className="h-[104px] w-auto mx-auto mb-4 drop-shadow-lg" />
          <h1 
            className="text-3xl font-bold text-white"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Panel de Administración
          </h1>
          <p className="text-slate-400 mt-2">OkChat Platform</p>
        </div>

        <Card className="bg-slate-800 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Iniciar Sesión
            </CardTitle>
            <CardDescription className="text-slate-400">
              Ingresa tus credenciales de administrador
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-200">
                  Correo electrónico
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="admin@ejemplo.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500 focus:border-violet-500"
                  data-testid="admin-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-200">
                  Contraseña
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500 focus:border-violet-500 pr-10"
                    data-testid="admin-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-300"
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>
              <Button
                type="submit"
                className="w-full bg-red-600 hover:bg-red-700 text-white"
                disabled={loading}
                data-testid="admin-login-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Iniciando sesión...
                  </>
                ) : (
                  "Iniciar Sesión"
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="text-center text-slate-500 text-sm mt-6">
          <a href="/" className="hover:text-violet-400 transition-colors">
            ← Volver al inicio
          </a>
        </p>
      </div>
    </div>
  );
};

export default AdminLogin;
